/**
 * Shell FAB coordination — keeps the bottom-right floating widget
 * (`<ChatBubble>` / `<TesterNotesBubble>`, both `position: fixed; z-50`)
 * out of the way of the route content it floats over.
 *
 * Two independent concerns:
 *
 * 1. `useShellFabSafeArea(ref)` — a hosted route with a bottom-docked element
 *    (chat composer, sticky action bar) lifts the FAB clear of it by writing
 *    the element's height into the `--shell-fab-safe-area` CSS var on `:root`.
 *    Both bubbles add that var to their `bottom` offset (default `0px`).
 *    Lifted from `frontend/apps/ai/src/hooks/useShellFabSafeArea.ts`
 *    (2026-10-12, ai live visual QA: the 56px FAB sat on the capitan chat
 *    composer's Send button at 375 + 1440px).
 *
 * 2. `useForeignModalOpen(rootRef)` — the FAB is `z-50`, the same layer as
 *    every fleet modal/drawer (ui-core `<Modal>`, ai `BacklogItemDrawer`, …).
 *    It renders AFTER the route outlet, so at equal z-index it paints on top
 *    of an open dialog and steals clicks at 375px (2026-10-12 ai axe/visual
 *    run). The shell hides the FAB while any `aria-modal="true"` element that
 *    is NOT the widget's own panel is open. Fleet modals uniformly mark
 *    themselves `aria-modal="true"` (ui-core Modal, 200+ app dialogs), so the
 *    attribute is the contract — no per-app wiring needed.
 *
 * @layer layout
 * @package @plerity/dashboard-shell
 */

import { type RefObject, useLayoutEffect, useSyncExternalStore } from "react";

export const SHELL_FAB_SAFE_AREA_VAR = "--shell-fab-safe-area";

/**
 * Lift the shell FAB above `ref`'s element while it is mounted (and `active`).
 * Tracks the element's height with a ResizeObserver (auto-growing textareas)
 * and clears the var on unmount so other routes are unaffected.
 */
export function useShellFabSafeArea(ref: RefObject<HTMLElement | null>, active = true): void {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!active || !el) return;
    const root = document.documentElement;
    const apply = () => root.style.setProperty(SHELL_FAB_SAFE_AREA_VAR, `${Math.ceil(el.offsetHeight)}px`);
    apply();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(apply) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      root.style.removeProperty(SHELL_FAB_SAFE_AREA_VAR);
    };
  }, [ref, active]);
}

const MODAL_SELECTOR = '[aria-modal="true"]';

function isRendered(el: Element): boolean {
  if (el.closest("[hidden]")) return false;
  if (typeof window === "undefined" || typeof window.getComputedStyle !== "function") return true;
  const style = window.getComputedStyle(el);
  return style.display !== "none" && style.visibility !== "hidden";
}

/**
 * True when an `aria-modal="true"` element is open anywhere in the document
 * OUTSIDE `exclude` (the FAB's own subtree — the assistant `<ChatPanel>` is
 * itself `aria-modal`, and must not hide its own launcher).
 */
export function isForeignModalOpen(exclude?: Element | null, doc: Document | undefined = globalThis.document): boolean {
  if (!doc) return false;
  for (const el of Array.from(doc.querySelectorAll(MODAL_SELECTOR))) {
    if (exclude?.contains(el)) continue;
    if (isRendered(el)) return true;
  }
  return false;
}

/**
 * Subscribe to `aria-modal` dialogs opening/closing anywhere in `<body>`.
 * Re-evaluates on DOM insertions/removals and `aria-modal` / `hidden` /
 * `style` / `class` attribute flips (class+style cover CSS-toggled drawers).
 */
export function useForeignModalOpen(rootRef: RefObject<Element | null>): boolean {
  return useSyncExternalStore(
    (onChange) => {
      if (typeof MutationObserver === "undefined" || typeof document === "undefined") return () => {};
      const mo = new MutationObserver(onChange);
      mo.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["aria-modal", "hidden", "style", "class"],
      });
      return () => mo.disconnect();
    },
    () => isForeignModalOpen(rootRef.current),
    () => false
  );
}
